// src/components/SuperAdmin/SuperAdminPanel.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n';
import { superAdminT } from '../../i18n/superAdminTranslations';

const API = '/api';
const MEDIA = process.env.REACT_APP_API_URL || 'https://tzone-web-backend-1-2ev5.onrender.com';

const ROLE_LABEL = {
  trainer: { label: 'Entrenador', color: '#4CAF50' },
  athlete: { label: 'Atleta', color: '#2196F3' },
  admin: { label: 'Admin', color: '#FF9800' },
  moderator: { label: 'Moderador', color: '#9C27B0' },
  trainer_admin: { label: 'Trainer Admin', color: '#00BCD4' },
  superadmin: { label: 'Super Admin', color: '#F44336' },
};

const STATUS_COLOR = {
  activo: '#4CAF50',
  bloqueado: '#F44336',
  pendiente: '#FF9800',
};

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// Avatar con foto o iniciales como fallback
function Avatar({ user }) {
  const [imgError, setImgError] = useState(false);
  const hasPhoto = user.profilePhoto &&
    user.profilePhoto !== '/uploads/athletes/default.jpg' &&
    !imgError;

  const initials = `${(user.firstName || '')[0] || ''}${(user.lastName || '')[0] || ''}`.toUpperCase() || '?';
  const color = ROLE_LABEL[user.role]?.color || '#555';
  const resolvePhoto = (p) => {
    if (!p) return null;
    if (p.startsWith('data:')) return p;
    if (p.startsWith('http')) return p;
    if (p.startsWith('/')) return `${MEDIA}${p}`;
    return `${MEDIA}/uploads/trainers/${p}`;
  };
  const photoUrl = resolvePhoto(user.profilePhoto);

  return hasPhoto ? (
    <img
      src={photoUrl}
      alt={initials}
      onError={() => setImgError(true)}
      style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${color}33` }}
    />
  ) : (
    <div style={{
      width: 34, height: 34, borderRadius: '50%',
      background: color + '22', border: `2px solid ${color}44`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 700, color,
    }}>
      {initials}
    </div>
  );
}

export default function SuperAdminPanel() {
  const { language } = useTranslation();
  const sa = (key) => (superAdminT[language] || superAdminT['es'])[key] || key;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [search, setSearch] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterRole) params.role = filterRole;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/admin/superadmin/users`, {
        headers: headers(), params
      });
      setUsers(data.users || []);
    } catch (e) {
      setError(e?.response?.data?.message || sa('loadError'));
    } finally {
      setLoading(false);
    }
  }, [filterRole, search]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const doAction = async (action, userId) => {
    try {
      if (action === 'block') {
        await axios.post(`${API}/admin/superadmin/users/${userId}/block`, {}, { headers: headers() });
        showToast('Usuario bloqueado');
      } else if (action === 'unblock') {
        await axios.post(`${API}/admin/superadmin/users/${userId}/unblock`, {}, { headers: headers() });
        showToast('Usuario activado');
      } else if (action === 'delete') {
        await axios.delete(`${API}/admin/superadmin/users/${userId}`, { headers: headers() });
        showToast('Usuario eliminado');
      }
      fetchUsers();
    } catch (e) {
      showToast(e?.response?.data?.message || sa('actionError'), 'error');
    } finally {
      setConfirmAction(null);
    }
  };

  const toggleLab = async (userId, current) => {
    try {
      await axios.patch(
        `${API}/admin/superadmin/users/${userId}/lab-access`,
        { labAccess: !current },
        { headers: headers() }
      );
      showToast(!current ? 'Acceso LAB activado' : 'Acceso LAB revocado');
      fetchUsers();
    } catch (e) {
      showToast('Error actualizando acceso LAB', 'error');
    }
  };

  const s = {
    wrap: { minHeight: '100vh', background: '#0f1117', color: '#e1e0d9', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    topbar: { background: '#193255', padding: '14px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #0d2245' },
    title: { fontSize: 18, fontWeight: 600, color: '#fff', margin: 0 },
    badge: { background: '#f7941d', color: '#111', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, marginLeft: 10 },
    content: { padding: '24px 28px' },
    filters: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
    input: { background: '#1a1a2e', border: '0.5px solid #2a2a4a', borderRadius: 8, padding: '8px 14px', color: '#e1e0d9', fontSize: 13, outline: 'none', minWidth: 200 },
    select: { background: '#1a1a2e', border: '0.5px solid #2a2a4a', borderRadius: 8, padding: '8px 14px', color: '#e1e0d9', fontSize: 13, outline: 'none' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 14px', textAlign: 'left', color: '#6b7280', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '0.5px solid #1e1e3a' },
    td: { padding: '12px 14px', borderBottom: '0.5px solid #1a1a2e', verticalAlign: 'middle' },
    rolePill: (role) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: (ROLE_LABEL[role]?.color || '#555') + '22', color: ROLE_LABEL[role]?.color || '#aaa' }),
    statusDot: (st) => ({ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLOR[st] || '#666', display: 'inline-block', marginRight: 6 }),
    btnBlock: { background: '#1e1a0e', color: '#f59e0b', border: '0.5px solid #f59e0b44', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    btnUnblock: { background: '#0e1e0e', color: '#4CAF50', border: '0.5px solid #4CAF5044', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    btnDelete: { background: '#1e0e0e', color: '#ef4444', border: '0.5px solid #ef444444', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    labOn: { background: '#0e2e1e', color: '#4CAF50', border: '0.5px solid #4CAF5044', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    labOff: { background: '#1a1a1a', color: '#555', border: '0.5px solid #333', borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' },
    modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 },
    modalBox: { background: '#1a1a2e', border: '0.5px solid #2a2a4a', borderRadius: 12, padding: 28, maxWidth: 360, width: '90%' },
    toastWrap: { position: 'fixed', bottom: 24, right: 24, zIndex: 9999 },
    toastBox: (type) => ({ background: type === 'error' ? '#1e0e0e' : '#0e1e0e', border: `0.5px solid ${type === 'error' ? '#ef4444' : '#4CAF50'}`, color: type === 'error' ? '#ef4444' : '#4CAF50', borderRadius: 8, padding: '10px 18px', fontSize: 13 }),
    counter: { fontSize: 12, color: '#555', marginLeft: 'auto' },
  };

  const counts = {
    total: users.length,
    trainers: users.filter(u => u.role === 'trainer').length,
    lab: users.filter(u => u.labAccess).length,
    bloqueados: users.filter(u => u.status === 'bloqueado').length,
  };

  return (
    <div style={s.wrap}>
      {/* TOPBAR */}
      <div style={s.topbar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>⚡</span>
          <span style={s.title}>Super Admin</span>
          <span style={s.badge}>TZone</span>
        </div>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: 'rgba(255,255,255,.6)' }}>
          <span>👥 {counts.total} {'usuarios'}</span>
          <span>🎯 {counts.trainers} {'entrenadores'}</span>
          <span>🧪 {counts.lab} {'con LAB'}</span>
          <span style={{ color: '#ef4444' }}>🚫 {counts.bloqueados} {'bloqueados'}</span>
          <button onClick={() => { localStorage.clear(); window.location.href = '/login'; }} style={{ background: "transparent", color: "rgba(255,255,255,.5)", border: "0.5px solid rgba(255,255,255,.2)", borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "inherit", marginLeft: 16 }}>{sa('logout')}</button>
        </div>
      </div>

      <div style={s.content}>
        {/* FILTROS */}
        <div style={s.filters}>
          <input
            style={s.input}
            placeholder={sa('searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={s.select} value={filterRole} onChange={e => setFilterRole(e.target.value)}>
            <option value="">{sa('allRoles')}</option>
            <option value="trainer">Entrenadores</option>
            <option value="athlete">Atletas</option>
            <option value="admin">Admin</option>
            <option value="moderator">Moderador</option>
            <option value="trainer_admin">Trainer Admin</option>
            <option value="superadmin">Super Admin</option>
          </select>
          <button
            onClick={fetchUsers}
            style={{ background: '#193255', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {sa('refresh')}
          </button>
          <span style={s.counter}>{users.length} {sa('results')}</span>
        </div>

        {/* ERROR */}
        {error && (
          <div style={{ background: '#1e0e0e', border: '0.5px solid #ef4444', color: '#ef4444', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* TABLA */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>Cargando...</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}></th>
                  <th style={s.th}>Usuario</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Rol</th>
                  <th style={s.th}>Estado</th>
                  <th style={s.th}>{'LAB'}</th>
                  <th style={s.th}>Registro</th>
                  <th style={s.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#555', padding: 40 }}>
                      {sa('noUsers')}
                    </td>
                  </tr>
                ) : users.map(u => (
                  <tr key={u._id} style={{ opacity: u.status === 'bloqueado' ? 0.6 : 1 }}>
                    <td style={{ ...s.td, width: 44 }}>
                      <Avatar user={u} />
                    </td>
                    <td style={s.td}>
                      <div style={{ fontWeight: 600, color: '#e1e0d9' }}>
                        {u.firstName} {u.lastName}
                      </div>
                      <div style={{ fontSize: 11, color: '#555' }}>@{u.username}</div>
                    </td>
                    <td style={s.td}>
                      <span style={{ color: '#6b7280' }}>{u.email}</span>
                    </td>
                    <td style={s.td}>
                      <span style={s.rolePill(u.role)}>
                        {ROLE_LABEL[u.role]?.label || u.role}
                      </span>
                    </td>
                    <td style={s.td}>
                      <span style={s.statusDot(u.status)} />
                      <span style={{ color: STATUS_COLOR[u.status] || '#666', fontSize: 12 }}>
                        {u.status || 'activo'}
                      </span>
                    </td>
                    <td style={s.td}>
                      {['trainer', 'trainer_admin'].includes(u.role) ? (
                        <button
                          style={u.labAccess ? s.labOn : s.labOff}
                          onClick={() => toggleLab(u._id, u.labAccess)}
                        >
                          {u.labAccess ? '✓ LAB' : '+ LAB'}
                        </button>
                      ) : (
                        <span style={{ color: '#333', fontSize: 11 }}>—</span>
                      )}
                    </td>
                    <td style={s.td}>
                      <span style={{ fontSize: 11, color: '#555' }}>
                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString('es-CL') : '—'}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {u.role !== 'superadmin' && (
                          <>
                            {u.status === 'bloqueado' ? (
                              <button style={s.btnUnblock} onClick={() => doAction('unblock', u._id)}>
                                {sa('unblock')}
                              </button>
                            ) : (
                              <button style={s.btnBlock} onClick={() => doAction('block', u._id)}>
                                {sa('block')}
                              </button>
                            )}
                            <button
                              style={s.btnDelete}
                              onClick={() => setConfirmAction({ action: 'delete', userId: u._id, name: `${u.firstName} ${u.lastName}` })}
                            >
                              {sa('delete')}
                            </button>
                          </>
                        )}
                        {u.role === 'superadmin' && (
                          <span style={{ fontSize: 11, color: '#555' }}>{sa('protected')}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL CONFIRMAR ELIMINACIÓN */}
      {confirmAction && (
        <div style={s.modal}>
          <div style={s.modalBox}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#e1e0d9', marginBottom: 10 }}>
              {sa('confirmDeleteTitle')}
            </div>
            <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
              {`¿Eliminar a ${confirmAction.name}? Esta acción no se puede deshacer.`}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                style={{ ...s.btnDelete, padding: '8px 20px', fontSize: 13 }}
                onClick={() => doAction('delete', confirmAction.userId)}
              >
                {sa('confirmDeleteBtn')}
              </button>
              <button
                style={{ background: '#1a1a2e', color: '#9ca3af', border: '0.5px solid #2a2a4a', borderRadius: 6, padding: '8px 20px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}
                onClick={() => setConfirmAction(null)}
              >
                {sa('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div style={s.toastWrap}>
          <div style={s.toastBox(toast.type)}>{toast.msg}</div>
        </div>
      )}
    </div>
  );
}
